import { Component, computed, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Role } from 'src/app/models/user/role.model';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { LenderOrganizationsService } from 'src/app/services/lender.organizations.service';
import { ProductsService } from 'src/app/services/products.service';
import { UsersService } from 'src/app/services/users.service';
import { DeleteConfirmationDialogService } from 'src/app/services/delete-confirmation-dialog.service';
import { Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { toSignal } from '@angular/core/rxjs-interop';
import { ISelect } from 'src/app/modules/shared/components/mat-multiselect/mat-multiselect.component';
import { baseColDef } from 'src/app/modules/shared/utils';
import { BtnCellRenderer } from 'src/app/modules/shared/components/btn-cell-renderer/btn-cell-renderer.component';
import { BaseListComponent } from '../../../components/base-list.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SearchSortComponent } from '../../../components/search-sort/search-sort.component';
import { AgGridWrapperComponent } from '../../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TextTooltipRenderer } from '../../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { FormStatusCellRenderer } from '../../../components/form-status/form-status-cell-renderer.component';
import { MultiBadgeCellRenderer } from '../../../components/multi-badge-cell-renderer/multi-badge-cell-renderer.component';
import { formatPhone } from '../../../utils/phone.util';
import { MatCheckboxModule } from '@angular/material/checkbox';

@Component({
    selector: 'app-lender-organization-list',
    templateUrl: './lender-organization-list.component.html',
    styleUrls: ['./lender-organization-list.component.scss'],
    standalone: true,
    imports: [
    MatSidenavModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    SearchSortComponent,
    AgGridWrapperComponent,
    MatPaginatorModule,
    MatIconModule,
    FormsModule,          
    MatCheckboxModule
]
})
export class LenderOrganizationListComponent extends BaseListComponent<any> implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer: MatDrawer;

  productList: ISelect[] = [];
  isEditing = false;
  id: number;
  datePipe = new DatePipe('en-US');
  subscriptions: Subscription[] = [];
  showAll: boolean = false;

  override currentSortField = {
    sortValue: 'name',
  };

  currentPageSize: number = 20;

  sortOptions = [
    { label: 'Name', sortValue: 'name' },
    { label: 'Acronym', sortValue: 'acronym' },

  ];

  searchKey: string = '';

  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider= computed(() => this.userRole() === Role.Provider);
  readonly isSystemUser = computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant= computed(() => this.userRole() === Role.Consultant);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);
  readonly isLenderReadOnly = computed(() => this.userRole() === Role.LenderReadOnly);

  gridAPI: GridApi;

public columnDefs: ColDef[] = [
  {
    ...baseColDef,
    field: 'name',
    headerName: 'Name',
    flex: 2,
    cellRenderer: TextTooltipRenderer,
    cellRendererParams: {
      onClick: this.onView.bind(this)
    },
     cellStyle: (params): any => {
      if (params.data?.isChildRow) {
        return { paddingLeft: '40px' };
      }
      if (params.data?.isParentRow || (!params.data?.isChildRow && !params.data?.parentOrganizationId)) {
        return { fontWeight: 'bold' };  
      }
      return null;
    }
  },
  { 
    ...baseColDef, 
    field: 'acronym', 
    headerName: 'Acronym',
    flex: 2 
  },
  {
    ...baseColDef,
    field: 'email',
    headerName: 'Email',
    cellRenderer: TextTooltipRenderer,
    flex: 2
  },
  {
    ...baseColDef,
    field: 'phoneNumber',
    headerName: 'Phone Number',
    cellRenderer: TextTooltipRenderer,
    valueFormatter: (params) => formatPhone(params.value),
    flex: 2
  },
  {
    ...baseColDef,
    field: 'physicalAddress',
    headerName: 'Address',
    cellRenderer: TextTooltipRenderer,
    valueGetter: (params) => {
      const data = params.data;
      if (!data) return '';
      
      const parts = [];
      if (data.mailingStreetAddress) parts.push(data.mailingStreetAddress);
      if (data.mailingStreetAddressLine2) parts.push(data.mailingStreetAddressLine2);
      if (data.mailingCity) parts.push(data.mailingCity);
      if (data.mailingStateName) parts.push(data.mailingStateName);
      if (data.mailingZipCode) parts.push(data.mailingZipCode);
      
      return parts.length > 0 ? parts.join(', ') : '';
    },
    flex: 4
  },
  {
    ...baseColDef,
    field: 'products',
    headerName: 'Products',
    cellRenderer: MultiBadgeCellRenderer,
    valueGetter: (params) => {
      return params?.data?.products?.map((ps: any) => ps.product)
    },
    flex: 2
  },
  {
    ...baseColDef,
    field: 'isActive',
    valueGetter: (params) => {
      return params?.data?.isActive ? {
        statusId: 5,
        name: 'Enabled'
      }: {
        statusId: 6,
        name: 'Disabled'
      }
    },
    headerName: 'Status',
    width: 120,
    cellRenderer: FormStatusCellRenderer,
    comparator: (a, b) => (a?.statusId ?? 0) - (b?.statusId ?? 0),
  },
];

  constructor(
    private service: LenderOrganizationsService,
    private productService: ProductsService,
    private userService: UsersService,
    private deleteDialog: DeleteConfirmationDialogService,
    private router: Router,
  ) {
    super()
  }

  ngOnInit() {
    const productSuscription = this.productService.get().subscribe((response: any) => {
      if (!response.isError) {
        this.productList = [...response.data.items];
      } else {
        this.productList = [];
      }
    });
    this.subscriptions.push(productSuscription)

    const searchControlSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        tap(() => {
          this.currentPage = 0;
          this.reload();
        }),
      )
      .subscribe();

    this.subscriptions.push(searchControlSubscription)

  }

  goToUpdate(data: any = null) {
    if (data) {
      this.id = data.id;
      this.router.navigate([`/admin/lender-organization/create/${this.id}`]);
    } else {
      this.router.navigate([`/admin/lender-organization/create`]);
    }
  }

  popupUpdate(data: any) {
    this.reload();
  }

  onConfirmation(result: any) {
    if (result.confirmed) {
      this.service.delete(result.id).subscribe(() => {
        this.reload();
      });
    }
  }

 onView(data: any) {
  if (this.isAdmin() || this.isSystemUser()) {
    this.router.navigate([`/admin/lender-organization/details/${data.id}`]);
  } else if (this.isProvider() || this.isConsultant() || this.isProviderReadOnly()) {
    this.router.navigate([`/provider/lender-organization/details/${data.id}`]);
  } else if (this.isLender() || this.isLenderReadOnly()) {
    this.router.navigate([`/lender/lender-organization-list/${data.id}`]);
  }
}

  onChangeAvailability(data: any) {
    const hasChildren = data.hasChildren === true;
    const organizationType = hasChildren 
      ? 'Lender organization and all its child organizations' 
      : 'Lender organization';
  
    const actionMessage = data.isActive 
      ? `This ${organizationType} will be disabled.`
      : `This ${organizationType} will be enabled.`;
  
    this.deleteDialog
      .openDeleteConfirmation(
      actionMessage,
        data.isActive ? 'Disable confirmation' : 'Enable confirmation',
        data.isActive ? 'Disable' : 'Enable'
      )
      .afterClosed()
      .pipe(
        switchMap(dialogClosed => {
          if(dialogClosed)
            return this.service.changeUserAvailability(data.id, !data.isActive);
          else
            return of(false);
        })
      )
      .subscribe((deleteConfirmation) => {
        if (deleteConfirmation) {
          this.reload();
        }
      });
  }
private transformDataToHierarchy(items: any[]): any[] {
  if (!items || items.length === 0) return [];
  
  const result: any[] = [];
  const processedIds = new Set<number>();
  
  const parentIds = new Set<number>();
  const childrenByParent = new Map<number, any[]>();
  
  items.forEach(item => {
    if (item.hasChildren && !item.parentOrganizationId) {
      parentIds.add(item.id);
    }
    if (item.parentOrganizationId) {
      if (!childrenByParent.has(item.parentOrganizationId)) {
        childrenByParent.set(item.parentOrganizationId, []);
      }
      childrenByParent.get(item.parentOrganizationId)!.push(item);
    }
  });
  
  items.forEach(item => {
    if (processedIds.has(item.id)) return;
    
    // Orphaned child (parent not on this page)
    if (item.parentOrganizationId && !parentIds.has(item.parentOrganizationId)) {
      result.push({
        ...item,
        isChildRow: true,
        isParentRow: false
      });
      processedIds.add(item.id);
      return;
    }
    
    // Child whose parent IS on this page - skip, will be added with parent
    if (item.parentOrganizationId) {
      return;
    }
    
    // Parent or standalone
    result.push({
      ...item,
      isParentRow: item.hasChildren,
      isChildRow: false
    });
    processedIds.add(item.id);
    
    // Add children immediately after parent
    if (childrenByParent.has(item.id)) {
      childrenByParent.get(item.id)!.forEach(child => {
        if (!processedIds.has(child.id)) {
          result.push({
            ...child,
            isChildRow: true,
            isParentRow: false
          });
          processedIds.add(child.id);
        }
      });
    }
  });
  
  return result;
}

public getRowStyle = (params: any) => {
  if (params.data?.isParentRow) {
    return { background: '#f5f8fa' };  
  }
  return { background: '#ffffff' }; 
};
  //-------------GRID FUNCTIONS

  onGridReady(params: GridReadyEvent) {
    this.gridAPI = params.api;
    this.reload();
    this.adjustColumnVisibility(params.api);
  }

  reload(backward: boolean = false) {
    this.searchKey = this.searchControl.value?.toLowerCase() || '';

    let filters: any = {
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      forward: !backward,
      page: this.currentPage + 1,
      pageSize: this.pageSize,
      searchKey: this.searchKey
    };

    if (!this.showAll) {
    filters.isActive = true;
   }

    this.gridAPI.showLoadingOverlay();
    this.service
      .get(filters)
      .pipe(
        switchMap((response: any) => {
          if (!response.isError) {
            return of(response.data);
          } else {
            return of([]);
          }
        })
      )
      .subscribe((data: any) => {
      this.gridAPI.hideOverlay();
      this.totalCount = data.totalCount;
      this.rowData = this.transformDataToHierarchy(data.items);
      });
  }

onShowAllChange(): void {
  this.currentPage = 0;
  this.reload();
}
  private adjustColumnVisibility(gridApi: any) {
    let columns = undefined;

    if(this.isAdmin())
    {
      const actionCol = {
        field: 'action',
        minWidth: 80,
        maxWidth: 80,
        filter: false,
        sortable: false,
        cellRenderer: BtnCellRenderer,
        cellRendererParams: {
          onEdit: this.goToUpdate.bind(this),
          onChangeAvailability: this.onChangeAvailability.bind(this),
        },
      };

      columns = [...this.columnDefs, actionCol];
    }
    else if(this.isProvider() || this.isConsultant() || this.isProviderReadOnly())
    {
      columns = this.columnDefs.filter(col => col.field !== 'isActive');
    }
    else
    {
      columns = this.columnDefs;
    }

    gridApi.setColumnDefs(columns);
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }
}
